import React, { useState } from "react";
import { SectionHeader } from "@/components/ui/section-header";
import { UseFormReturn } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { GlassCard, GlassCardHeader, GlassCardTitle, GlassCardDescription, GlassCardContent } from "@/components/ui/glass-card";
import { Button } from "@/components/ui/button";
import { Plus, RefreshCw, FilePlus, Trash, Loader2, AlertTriangle, Clock, CheckCircle } from "lucide-react";
import { PatchPolicyFormValues } from "@/components/patching/schema";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ActionsDropdown } from "@/components/ui/actions-dropdown";
import { ApnsConfigurationList } from "@/components/patching/sections/settings/apns/ApnsConfigurationList";
import { toast } from "sonner";
import { useAbmConnections, AbmConnection } from "@/hooks/useAbmConnections";
import { Badge } from "@/components/ui/badge";
import { AddAbmConfigDialog } from "./AddAbmConfigDialog";
import { deleteConfiguration } from "@/services/adeApi";
import { AppBookSection } from "./appbook/AppBookSection";

interface AppleSettingsProps {
  form: UseFormReturn<PatchPolicyFormValues>;
}

export function AppleSettings({ form }: AppleSettingsProps) {
  const navigate = useNavigate();
  const { connections, loading, error, fetchConnections } = useAbmConnections();

  const [createProfileDialogOpen, setCreateProfileDialogOpen] = useState(false);
  const [addAbmDialogOpen, setAddAbmDialogOpen] = useState(false);
  const [selectedAbmConfigForProfile, setSelectedAbmConfigForProfile] = useState<AbmConnection | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AbmConnection | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleCreateEnrollmentProfile = (config: AbmConnection) => {
    setSelectedAbmConfigForProfile(config);
    setCreateProfileDialogOpen(true);
  };

  const confirmNavigateToEnrollmentProfiles = () => {
    setCreateProfileDialogOpen(false);
    navigate("/admin/enrollment-profiles");
  };

  const syncAbmConfig = (config: AbmConnection) => {
    toast.info(`Syncing ${config.name}...`);
    setTimeout(() => {
      toast.success(`${config.name} synced successfully`);
    }, 1500);
  };

  const handleDeleteConfig = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteConfiguration(deleteTarget.id);
      toast.success("ABM configuration deleted");
      setDeleteTarget(null);
      fetchConnections();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete ABM configuration");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <GlassCard variant="default" hover={false}>
      <GlassCardHeader>
        <GlassCardTitle>Apple Configuration</GlassCardTitle>
        <GlassCardDescription>
          Configure Apple Push Notification certificates and Apple Business Manager integration
        </GlassCardDescription>
      </GlassCardHeader>
      <GlassCardContent className="space-y-8">
        {/* APNS Settings Section */}
        <section className="space-y-4">
          <ApnsConfigurationList />
        </section>

        {/* ABM Settings Section */}
        <section className="space-y-4 pt-6 border-t">
          <SectionHeader
            title="Apple Business Manager Configuration"
            buttonLabel="Add ABM Configuration"
            onButtonClick={() => setAddAbmDialogOpen(true)}
          />
        
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading ABM configurations...</span>
            </div>
          ) : error ? (
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              <p className="text-sm text-destructive mb-2">Failed to load ABM configurations</p>
              <p className="text-xs text-muted-foreground mb-4">{error.message}</p>
              <Button variant="outline" size="sm" onClick={() => fetchConnections()}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </div>
          ) : connections.length === 0 ? (
            <div className="border-2 border-dashed rounded-lg p-12 text-center">
              <h3 className="text-lg font-semibold mb-1">No ABM Configurations Found</h3>
              <p className="text-sm text-muted-foreground">
                No Apple Business Manager accounts have been configured yet.
              </p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {connections.map(config => (
                <GlassCard key={config.id} variant="subtle" hover={true} className="overflow-hidden">
                  <div className="p-4 space-y-2">
                    <div className="flex justify-between items-center">
                      <h3 className="font-medium text-lg">{config.name || 'Unnamed Account'}</h3>
                      <ActionsDropdown
                        actions={[
                          {
                            label: "Sync",
                            icon: RefreshCw,
                            onClick: () => syncAbmConfig(config),
                          },
                          {
                            label: "Create Enrollment Profile",
                            icon: FilePlus,
                            onClick: () => handleCreateEnrollmentProfile(config),
                          },
                          {
                            label: "Delete",
                            icon: Trash,
                            onClick: () => setDeleteTarget(config),
                            destructive: true,
                          },
                        ]}
                      />
                    </div>
                    <div className="text-sm text-muted-foreground space-y-1">
                      {config.organization_name && (
                        <span className="block">Organization: {config.organization_name}</span>
                      )}
                      {config.is_active !== null && (
                        <Badge variant={config.is_active ? "default" : "secondary"} className="mt-1">
                          {config.is_active ? "Active" : "Inactive"}
                        </Badge>
                      )}
                      {config.token_expiry && (() => {
                        const expiryDate = new Date(config.token_expiry);
                        const now = new Date();
                        const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / 86400000);

                        if (daysUntilExpiry < 0) {
                          return (
                            <Badge variant="destructive" className="mt-1 gap-1">
                              <AlertTriangle className="h-3 w-3" />
                              Token expired {Math.abs(daysUntilExpiry)} days ago
                            </Badge>
                          );
                        }
                        if (daysUntilExpiry <= 14) {
                          return (
                            <Badge className="mt-1 gap-1 border-transparent bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
                              <AlertTriangle className="h-3 w-3" />
                              Expires in {daysUntilExpiry} days — renew now
                            </Badge>
                          );
                        }
                        if (daysUntilExpiry <= 30) {
                          return (
                            <Badge className="mt-1 gap-1 border-transparent bg-warning/10 text-warning dark:bg-warning/30 dark:text-warning">
                              <Clock className="h-3 w-3" />
                              Expires in {daysUntilExpiry} days
                            </Badge>
                          );
                        }
                        return (
                          <span className="mt-1 inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                            <CheckCircle className="h-3 w-3" />
                            Token expires {expiryDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </GlassCard>
              ))}
            </div>
          )}
        </section>

        {/* Apple Apps & Books Section */}
        <AppBookSection />

        {/* Create Enrollment Profile Confirmation Dialog */}
        <AlertDialog open={createProfileDialogOpen} onOpenChange={setCreateProfileDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Create Enrollment Profile</AlertDialogTitle>
              <AlertDialogDescription>
                You will be navigated to the Enrollment Profiles section where you can create a new enrollment profile
                {selectedAbmConfigForProfile && (
                  <> using the <strong>{selectedAbmConfigForProfile.name}</strong> ABM configuration.</>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmNavigateToEnrollmentProfiles}>
                Continue
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete ABM Configuration Confirmation Dialog */}
        <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete ABM Configuration</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? This will remove the
                Apple Business Manager connection and any associated enrollment data. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteConfig}
                disabled={deleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AddAbmConfigDialog
          open={addAbmDialogOpen}
          onOpenChange={setAddAbmDialogOpen}
          onSuccess={() => fetchConnections()}
        />
      </GlassCardContent>
    </GlassCard>
  );
}
